import React, { useCallback, useEffect, useState } from 'react';
import { Mail, Eye, Trash2, CheckCircle, MessageSquare } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../context/AdminContext';

const STATUS_CONFIG = {
  new:     { label: 'New',     color: 'bg-blue-100 text-blue-700',   icon: Mail },
  read:    { label: 'Read',    color: 'bg-yellow-100 text-yellow-700', icon: Eye },
  replied: { label: 'Replied', color: 'bg-green-100 text-green-700',  icon: CheckCircle },
};

const AdminContacts = () => {
  const { toast } = useToast();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);

  const loadContacts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.get('/contacts');
      setContacts(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load contacts', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  const updateStatus = async (id, status) => {
    try {
      const response = await adminApi.put(`/contacts/${id}/status`, { status });
      setSelectedContact(response.data);
      toast({ title: 'Updated', description: `Marked as ${status}` });
      loadContacts();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await adminApi.delete(`/contacts/${id}`);
      setSelectedContact(null);
      toast({ title: 'Deleted', description: 'Message removed' });
      loadContacts();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const handleView = (contact) => {
    setSelectedContact(contact);
    if (contact.status === 'new') updateStatus(contact.id, 'read');
  };

  const newCount = contacts.filter(c => c.status === 'new').length;

  return (
    <div data-testid="admin-contacts-page">
      <div className="mb-6">
        <h2 className="text-xl sm:text-3xl font-bold text-gray-900">Contact Messages</h2>
        <p className="text-gray-600 mt-1">{contacts.length} message{contacts.length !== 1 ? 's' : ''}{newCount > 0 ? ` (${newCount} new)` : ''}</p>
      </div>

      <Dialog open={!!selectedContact} onOpenChange={(open) => { if (!open) setSelectedContact(null); }}>
        <DialogContent className="max-w-lg">
          {selectedContact && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selectedContact.subject}
                  <Badge className={STATUS_CONFIG[selectedContact.status]?.color}>{STATUS_CONFIG[selectedContact.status]?.label}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2 text-sm bg-gray-50 rounded-lg p-3">
                  <div><span className="text-gray-500">Name:</span> {selectedContact.name}</div>
                  <div><span className="text-gray-500">Email:</span> {selectedContact.email}</div>
                  <div><span className="text-gray-500">Phone:</span> {selectedContact.phone || 'N/A'}</div>
                  <div><span className="text-gray-500">Date:</span> {new Date(selectedContact.created_at).toLocaleDateString()}</div>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Message</h4>
                  <p className="text-gray-700 bg-gray-50 rounded-lg p-4 text-sm whitespace-pre-wrap">{selectedContact.message}</p>
                </div>
                <div className="flex gap-2">
                  {['new', 'read', 'replied'].map(s => (
                    <Button key={s} size="sm" variant={selectedContact.status === s ? 'default' : 'outline'}
                      className={selectedContact.status === s ? 'bg-red-600 hover:bg-red-700' : ''}
                      onClick={() => updateStatus(selectedContact.id, s)}
                      disabled={selectedContact.status === s}
                    >{STATUS_CONFIG[s]?.label}</Button>
                  ))}
                  <Button size="sm" variant="destructive" className="ml-auto" onClick={() => handleDelete(selectedContact.id)}>
                    <Trash2 className="h-4 w-4 mr-1" />Delete
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div></div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <MessageSquare className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <p>No messages yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {contacts.map((contact) => (
            <Card key={contact.id} className={`cursor-pointer hover:shadow-md transition-shadow ${contact.status === 'new' ? 'border-l-4 border-l-blue-500' : ''}`}
              onClick={() => handleView(contact)} data-testid={`contact-item-${contact.id}`}>
              <CardContent className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${contact.status === 'new' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                  <Mail className={`h-5 w-5 ${contact.status === 'new' ? 'text-blue-600' : 'text-gray-400'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">{contact.name}</span>
                    <Badge className={`${STATUS_CONFIG[contact.status]?.color} text-xs`}>{STATUS_CONFIG[contact.status]?.label}</Badge>
                  </div>
                  <div className="text-sm font-medium text-gray-700 truncate">{contact.subject}</div>
                  <div className="text-xs text-gray-500 truncate">{contact.message}</div>
                </div>
                <div className="text-xs text-gray-400 shrink-0">{new Date(contact.created_at).toLocaleDateString()}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminContacts;
